import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PlusCircle } from "lucide-react";
import type { FormEvent } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { RecipeBoxFormValues } from "../_lib/recipeBoxForm";

export type CreateRecipeBoxDialogProps = Readonly<{
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<RecipeBoxFormValues>;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onFileChange: (file: File | null) => void;
  selectedFileName?: string;
}>;

export const CreateRecipeBoxDialog = ({
  isOpen,
  onOpenChange,
  form,
  isSubmitting,
  errorMessage,
  onSubmit,
  onFileChange,
  selectedFileName,
}: CreateRecipeBoxDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1">
          <PlusCircle className="h-3.5 w-3.5" />
          New Recipe Box
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-headline">Create New Recipe Box</DialogTitle>
          <DialogDescription>
            Give your new collection of recipes a name, description, and optional cover image.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="grid gap-4 py-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Summer Grilling" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="e.g., Our favorite recipes for the grill" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-2">
              <FormLabel htmlFor="create-cover">Cover Image</FormLabel>
              <Input
                id="create-cover"
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  onFileChange(file);
                }}
              />
              <p className="text-xs text-muted-foreground">
                Optional. Upload a JPG, PNG, or GIF up to 5 MB.
                {selectedFileName ? ` Selected: ${selectedFileName}` : ""}
              </p>
            </div>

            {errorMessage ? (
              <p className="text-sm text-destructive" role="alert">
                {errorMessage}
              </p>
            ) : null}

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Recipe Box"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

